import React, { useState } from "react";
import { cn } from "@shadcn-in-nx/utils";
import {
    CheckCircle, AlertCircle, FileText, ChevronDown, ChevronUp,
    Copy, Check, ExternalLink,
} from "lucide-react";
import { Button } from "@shadcn-in-nx/ui/button";

const StatusBadge = ({ success }) => (
    <span className={cn(
        "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium",
        success ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
    )}>
        {success ? <CheckCircle className="w-2.5 h-2.5" /> : <AlertCircle className="w-2.5 h-2.5" />}
        {success ? "OK" : "Error"}
    </span>
);

const CodeBlock = ({ code, language = "javascript", maxHeight = "200px" }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="relative group">
            <Button
                variant="ghost"
                size="sm"
                className="absolute top-1 right-1 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={handleCopy}
            >
                {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
            </Button>
            <pre className={cn(
                "text-[10px] bg-gray-900 text-gray-100 p-2 rounded overflow-auto",
                `max-h-[${maxHeight}]`
            )}>
                {code}
            </pre>
        </div>
    );
};

const FileResult = ({ file, defaultExpanded = false }) => {
    const [expanded, setExpanded] = useState(defaultExpanded);

    return (
        <div className="border rounded overflow-hidden mb-1">
            <button
                onClick={() => setExpanded(!expanded)}
                className="w-full flex items-center justify-between px-2 py-1.5 bg-gray-50 hover:bg-gray-100"
            >
                <div className="flex items-center gap-1.5">
                    <FileText className="w-3 h-3 text-blue-500" />
                    <span className="text-xs font-medium">{file.filePath || file.fileName}</span>
                    {file.found === false && (
                        <span className="text-[10px] text-red-500">Not Found</span>
                    )}
                </div>
                {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
            {expanded && file.code && (
                <div className="p-1.5 bg-white">
                    <CodeBlock code={file.code} maxHeight="300px" />
                </div>
            )}
        </div>
    );
};

const SearchMatch = ({ match, filePath }) => {
    const [expanded, setExpanded] = useState(false);

    return (
        <div className="border-l-2 border-amber-300 pl-2 py-1 mb-1">
            <div className="flex items-center gap-1.5 mb-0.5">
                <FileText className="w-2.5 h-2.5 text-gray-400" />
                <span className="text-[10px] font-medium text-gray-600">{filePath}</span>
                <span className="text-[10px] text-gray-400">({match.totalMatches})</span>
            </div>
            {match.matches?.slice(0, expanded ? undefined : 2).map((m, i) => (
                <pre key={i} className="text-[10px] bg-gray-50 p-1.5 rounded mt-0.5 overflow-x-auto">
                    {m.matchWithContext || m.line}
                </pre>
            ))}
            {match.matches?.length > 2 && (
                <button
                    onClick={() => setExpanded(!expanded)}
                    className="text-[10px] text-blue-600 hover:underline mt-0.5"
                >
                    {expanded ? "Less" : `+${match.matches.length - 2} more`}
                </button>
            )}
        </div>
    );
};

export default function ToolResponseView({ toolName, response }) {
    if (!response) return null;

    const isSuccess = response.success !== false && !response.error;

    const renderContent = () => {
        // Handle error responses
        if (response.error) {
            return (
                <div className="bg-red-50 border border-red-200 rounded p-2">
                    <div className="flex items-center gap-1.5 text-red-700 mb-1">
                        <AlertCircle className="w-3 h-3" />
                        <span className="text-xs font-medium">Error</span>
                    </div>
                    <pre className="text-[10px] text-red-600 whitespace-pre-wrap">
                        {typeof response.error === "string" ? response.error : JSON.stringify(response.error, null, 2)}
                    </pre>
                </div>
            );
        }

        switch (toolName) {
            case "read_file":
                return (
                    <div className="space-y-1">
                        {response.file && (
                            <FileResult file={response.file} defaultExpanded={true} />
                        )}
                    </div>
                );

            case "read_files":
                return (
                    <div className="space-y-1">
                        <div className="text-xs text-gray-600 mb-1">
                            {response.files?.filter(f => f.found).length || 0}/{response.files?.length || 0} files
                        </div>
                        {response.files?.map((file, i) => (
                            <FileResult key={i} file={file} defaultExpanded={response.files.length === 1} />
                        ))}
                    </div>
                );

            case "execute_code":
                return (
                    <div className="space-y-2">
                        {response.output !== undefined && (
                            <div>
                                <div className="text-[10px] font-medium text-gray-500 mb-0.5">Output</div>
                                <CodeBlock
                                    code={typeof response.output === "string" ? response.output : JSON.stringify(response.output, null, 2)}
                                />
                            </div>
                        )}
                        {response.logs?.length > 0 && (
                            <div>
                                <div className="text-[10px] font-medium text-gray-500 mb-0.5">Logs</div>
                                <pre className="text-[10px] bg-gray-100 p-1.5 rounded">
                                    {response.logs.join("\n")}
                                </pre>
                            </div>
                        )}
                    </div>
                );

            case "create_file":
            case "update_file":
                return (
                    <div className="space-y-1">
                        <div className="flex items-center gap-1.5 text-xs">
                            <FileText className="w-3 h-3 text-green-500" />
                            <span className="font-medium">{response.filePath}</span>
                        </div>
                        <div className="flex gap-1">
                            {response.changeType && (
                                <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                                    {response.changeType}
                                </span>
                            )}
                            {response.merged && (
                                <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">
                                    Merged
                                </span>
                            )}
                        </div>
                        {response.code && <CodeBlock code={response.code} />}
                    </div>
                );

            case "edit_file":
                return (
                    <div className="space-y-1">
                        <div className="text-xs text-gray-700">{response.message}</div>
                        {response.occurrences_replaced && (
                            <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                                {response.occurrences_replaced} replaced
                            </span>
                        )}
                    </div>
                );

            case "search_in_files":
            case "grep_search":
                return (
                    <div className="space-y-1">
                        <div className="flex items-center gap-2 text-xs text-gray-600 mb-1">
                            <span>{response.filesWithMatches || 0} files</span>
                            <span>{response.totalMatches || 0} matches</span>
                        </div>
                        {response.matches?.map((match, i) => (
                            <SearchMatch key={i} match={match} filePath={match.filePath} />
                        ))}
                    </div>
                );

            case "glob_search":
                return (
                    <div className="space-y-1">
                        <div className="text-xs text-gray-600">{response.message}</div>
                        <div className="space-y-0.5">
                            {response.matches?.slice(0, 10).map((file, i) => (
                                <div key={i} className="flex items-center gap-1.5 text-xs py-0.5 border-b border-gray-100">
                                    <FileText className="w-3 h-3 text-gray-400" />
                                    <span className="truncate">{file.filePath}</span>
                                </div>
                            ))}
                            {response.matches?.length > 10 && (
                                <div className="text-[10px] text-gray-500">+{response.matches.length - 10} more</div>
                            )}
                        </div>
                    </div>
                );

            case "explore_codebase":
            case "search_api":
                return (
                    <div className="space-y-2">
                        {response.answer && (
                            <div className="bg-blue-50 border border-blue-200 rounded p-2">
                                <div className="text-xs text-gray-800 whitespace-pre-wrap">{response.answer}</div>
                            </div>
                        )}
                        {response.filesSearched?.length > 0 && (
                            <div>
                                <div className="text-[10px] font-medium text-gray-500 mb-0.5">
                                    Files ({response.filesSearched.length})
                                </div>
                                <div className="flex flex-wrap gap-0.5">
                                    {response.filesSearched.slice(0, 8).map((f, i) => (
                                        <span key={i} className="text-[10px] bg-gray-100 px-1.5 py-0.5 rounded">
                                            {typeof f === "string" ? f.split("/").pop() : f}
                                        </span>
                                    ))}
                                    {response.filesSearched.length > 8 && (
                                        <span className="text-[10px] text-gray-500">+{response.filesSearched.length - 8}</span>
                                    )}
                                </div>
                            </div>
                        )}
                        {response.availableApis?.length > 0 && (
                            <div>
                                <div className="text-[10px] font-medium text-gray-500 mb-0.5">APIs</div>
                                {response.availableApis.slice(0, 5).map((api, i) => (
                                    <div key={i} className="bg-gray-50 rounded p-1.5 mb-0.5">
                                        <div className="font-medium text-xs">{api.name}</div>
                                        <div className="text-[10px] text-gray-500">{api.category}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );

            case "todo_write":
                return (
                    <div className="text-xs text-gray-700">
                        {response.message || "Todos updated"}
                    </div>
                );

            default:
                return (
                    <CodeBlock code={JSON.stringify(response, null, 2)} />
                );
        }
    };

    return (
        <div className="space-y-2">
            {renderContent()}
        </div>
    );
}
